/* Shared, pure text helpers for a step's coach question (bubbleContent) and
   the one-line summary shown on a past, collapsed turn (summaryText).
   No state here — they take the step (and answers) and return content. */
import * as React from 'react';
import { Answers, GENDERS, GROUPS, Option } from './data';
import { Step } from './flow';

export function bubbleContent(step: Step): React.ReactNode {
  switch (step.kind) {
    case 'welcome':
      return (
        <>
          <h1 className="intake-q">Welcome to the RealU online!</h1>
          <p className="intake-help">
            You will be making all your choices as if you are 26 years old. For real 26....think about it for a minute.
          </p>
        </>
      );
    case 'intro':
      return (
        <>
          <h1 className="intake-q">{step.title}</h1>
          <p className="intake-help">{step.subtitle}</p>
        </>
      );
    case 'eventCode':
      return (
        <>
          <h1 className="intake-q">{step.label}</h1>
          <p className="intake-help">It&apos;s on the board — or ask your teacher.</p>
        </>
      );
    case 'identity':
      return (
        <>
          <h1 className="intake-q">{step.label}</h1>
          <p className="intake-help">Type your name, then tap which one is you.</p>
        </>
      );
    case 'choice':
    case 'group':
    case 'session':
      return <h1 className="intake-q">{step.label}</h1>;
    case 'scale':
      return (
        <>
          <h1 className="intake-q">{step.label}</h1>
          <p className="intake-help">{step.help}</p>
        </>
      );
    case 'habit':
      return (
        <>
          <h1 className="intake-q">{step.habit!.prompt}</h1>
          <p className="intake-help">Which one sounds most like you?</p>
        </>
      );
    case 'score':
      return <h1 className="intake-q">Awesome work! Here&apos;s your RealU Score.</h1>;
    case 'careerPick':
    case 'kids':
    case 'kidsAges':
    case 'pets':
      return (
        <>
          <h1 className="intake-q">{step.label}</h1>
          {step.subtitle && <p className="intake-help">{step.subtitle}</p>}
        </>
      );
    case 'review':
      return <h1 className="intake-q">Well Done! We will see you soon!</h1>;
    default:
      return null;
  }
}

function genderLabel(v: string): string {
  const o = GENDERS.find((x) => x.value === v);
  return o ? (o.emoji ? o.emoji + ' ' : '') + o.label : '';
}

export function summaryText(step: Step, answers: Answers): string {
  switch (step.kind) {
    case 'eventCode':
      return answers._eventLoaded ? answers.accessCode.toUpperCase() : '';
    case 'group': {
      const g = GROUPS.find((x) => x.id === answers.teacherId);
      return g ? g.label : '';
    }
    case 'session': {
      const g = GROUPS.find((x) => x.id === answers.teacherId);
      const s = g && g.sessions.find((x) => x.id === answers.sessionId);
      return s ? s.label : '';
    }
    case 'identity':
      return `${(answers.firstName + ' ' + answers.lastName).trim()} · ${genderLabel(
        answers.gender
      )}`;
    case 'choice': {
      const v = String(answers[step.field!] ?? '');
      const o = step.options!.find((x: Option) => x.value === v);
      return o ? (o.emoji ? o.emoji + ' ' : '') + o.label : '';
    }
    case 'scale':
      return 'GPA ' + (typeof answers.gpa === 'number' ? answers.gpa.toFixed(2) : '—');
    case 'habit': {
      const sel = answers.habits[step.habit!.id];
      const o = step.habit!.options[sel];
      return o ? o.label : '';
    }
    case 'kids':
      if (answers.kidsCount == null) return '';
      return answers.kidsCount === 0
        ? 'No kids'
        : answers.kidsCount + (answers.kidsCount === 1 ? ' kid' : ' kids');
    case 'kidsAges':
      return answers.kidsAges.length ? 'Ages ' + answers.kidsAges.join(', ') : '';
    case 'pets':
      return answers._petsAnswered
        ? answers.pets.length
          ? answers.pets.join(', ')
          : 'No pets'
        : '';
    default:
      return '';
  }
}
